import { CapacitorUpdater, type BundleInfo } from "@capgo/capacitor-updater"
import { App } from "@capacitor/app"
import type { PluginListenerHandle } from "@capacitor/core"
import { Capacitor } from "@capacitor/core"
import { isPatchUpdate } from "@/services/app-update-policy"
import { showUpdateAvailableDialog } from "@/services/app-update-dialog"
import { isNativeStoreUpdateAvailable } from "./native-store-update-service"

const CAPGO_CHANNEL = import.meta.env.VITE_CAPGO_CHANNEL || "master"
const FOREGROUND_UPDATE_CHECK_INTERVAL_MS = 60 * 1000

let pendingBundle: BundleInfo | null = null
let updatePromptVisible = false
let listenersInitialized = false
let uiReady = false
let startupComplete = false
let listenerHandles: PluginListenerHandle[] = []
let foregroundUpdateCheckInterval: ReturnType<typeof setInterval> | null = null

function isDownloadedBundle(bundle: BundleInfo): boolean {
  return bundle.status === "success" || bundle.status === "pending"
}

async function getCurrentVersion(): Promise<string> {
  const { bundle } = await CapacitorUpdater.current()
  return bundle.version || "0.0.0"
}

async function findDownloadedBundle(): Promise<BundleInfo | null> {
  const { bundle: currentBundle } = await CapacitorUpdater.current()
  const { bundles } = await CapacitorUpdater.list()

  const downloadedBundles = bundles.filter(
    (bundle) =>
      isDownloadedBundle(bundle) && bundle.id !== currentBundle.id && bundle.id !== "builtin"
  )

  if (downloadedBundles.length === 0) {
    return null
  }

  return downloadedBundles.sort(
    (left, right) => Date.parse(right.downloaded) - Date.parse(left.downloaded)
  )[0]
}

function showUpdateDialog() {
  if (!pendingBundle || updatePromptVisible || !uiReady || !startupComplete) return
  if (isNativeStoreUpdateAvailable()) return

  updatePromptVisible = showUpdateAvailableDialog({
    version: pendingBundle.version,
    onConfirm: async () => {
      updatePromptVisible = false
      await applyPendingUpdate()
    },
    onCancel: () => {
      updatePromptVisible = false
    }
  })
}

async function applyPendingUpdate() {
  const bundleToApply = (await findDownloadedBundle()) ?? pendingBundle
  if (!bundleToApply) return

  pendingBundle = null

  await CapacitorUpdater.next({ id: bundleToApply.id })
  await CapacitorUpdater.reload()
}

async function handleDownloadedBundle(bundle: BundleInfo) {
  if (!uiReady || !startupComplete) {
    pendingBundle = bundle
    return
  }

  const currentVersion = await getCurrentVersion()

  if (isPatchUpdate(currentVersion, bundle.version)) {
    console.log("[Capgo] Patch update downloaded, will apply on next app start", bundle.version)
    return
  }

  pendingBundle = bundle
  showUpdateDialog()
}

async function checkForPendingUpdate() {
  if (!startupComplete) return

  const downloadedBundle = await findDownloadedBundle()
  if (!downloadedBundle) return

  const currentVersion = await getCurrentVersion()

  if (isPatchUpdate(currentVersion, downloadedBundle.version)) {
    return
  }

  await handleDownloadedBundle(downloadedBundle)
}

async function subscribeToCapgoChannel() {
  try {
    const { channel } = await CapacitorUpdater.getChannel()

    if (channel === CAPGO_CHANNEL) {
      console.log(`[Capgo] Device already subscribed to channel ${CAPGO_CHANNEL}`)
      return
    }

    const result = await CapacitorUpdater.setChannel({
      channel: CAPGO_CHANNEL,
      triggerAutoUpdate: true
    })

    console.log(`[Capgo] Subscribed device to channel ${CAPGO_CHANNEL}`, result)
  } catch (error) {
    console.error(`[Capgo] Failed to subscribe to channel ${CAPGO_CHANNEL}`, error)
  }
}

function startForegroundUpdateChecks() {
  stopForegroundUpdateChecks()

  foregroundUpdateCheckInterval = setInterval(() => {
    void CapacitorUpdater.triggerUpdateCheck()
    void checkForPendingUpdate()
  }, FOREGROUND_UPDATE_CHECK_INTERVAL_MS)
}

function stopForegroundUpdateChecks() {
  if (foregroundUpdateCheckInterval) {
    clearInterval(foregroundUpdateCheckInterval)
    foregroundUpdateCheckInterval = null
  }
}

async function onAppForegrounded() {
  await subscribeToCapgoChannel()
  await CapacitorUpdater.triggerUpdateCheck()

  if (startupComplete) {
    await checkForPendingUpdate()
    startForegroundUpdateChecks()
  }
}

async function onAppBackgrounded() {
  stopForegroundUpdateChecks()
}

async function processPendingBundleAfterStartup() {
  const downloadedBundle = pendingBundle ?? (await findDownloadedBundle())
  pendingBundle = null

  if (!downloadedBundle) return

  console.log("[Capgo] Applying update at startup", downloadedBundle.version)
  pendingBundle = downloadedBundle
  await applyPendingUpdate()
}

/**
 * Called once the splash screen is hidden and the app is ready for interaction.
 * All downloaded updates are applied silently at startup.
 */
export function markCapgoStartupComplete() {
  startupComplete = true
  startForegroundUpdateChecks()
  void processPendingBundleAfterStartup()
}

export async function initializeCapgoUpdatePrompt() {
  if (!Capacitor.isNativePlatform() || listenersInitialized) return

  listenersInitialized = true

  listenerHandles.push(
    await CapacitorUpdater.addListener("downloadComplete", ({ bundle }) => {
      console.log("[Capgo] downloadComplete", bundle.version)
      void handleDownloadedBundle(bundle)
    })
  )

  listenerHandles.push(
    await CapacitorUpdater.addListener("updateAvailable", ({ bundle }) => {
      console.log("[Capgo] updateAvailable", bundle.version)
    })
  )

  listenerHandles.push(
    await App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) {
        void onAppForegrounded()
      } else {
        void onAppBackgrounded()
      }
    })
  )

  uiReady = true
  await onAppForegrounded()
}

export async function teardownCapgoUpdatePrompt() {
  stopForegroundUpdateChecks()
  await Promise.all(listenerHandles.map((handle) => handle.remove()))
  listenerHandles = []
  listenersInitialized = false
  uiReady = false
  startupComplete = false
  pendingBundle = null
  updatePromptVisible = false
}
